import {
  all,
  call,
  put,
  takeLatest,
} from 'redux-saga/effects';

import KeychainStore from '../../../utils/KeychainStore';
import Biometrics from '../../../utils/Biometrics';
import { DEFAULT_ERROR_MSG } from '../../../utils/constants';
import { INVALID_BIOMETRICS_ENROLLMENT_KEYCHAIN } from '../../Main/constants';
import { updateShouldBiometricsOpenAutomatically } from '../../Main/actions';
import {
  changeStep,
  disableBiometricLoginFailure,
  disableBiometricLoginSuccess,
  disablePinFailure,
  disablePinSuccess,
  enableBiometricLoginFailure,
  enableBiometricLoginSuccess,
  setPinFailure,
  setPinSuccess,
  updateBiometricsAndPinInfo,
} from './actions';
import {
  BIOMETRICS_API_ERROR_MSG,
  CHECK_BIOMETRICS_AND_PIN_INFO,
  DISABLE_BIOMETRIC_LOGIN,
  DISABLE_PIN,
  ENABLE_BIOMETRIC_LOGIN,
  ENABLE_BIOMETRIC_LOGIN_ERROR_MSG,
  SET_PIN,
} from './constants';

export function* checkBiometricsAndPinInfoSaga() {
  try {
    const {
      hasBiometrics,
      isBiometricLoginEnabled,
      isPinEnabled,
    } = yield call(KeychainStore.determineMethodsEnabled);
    const supportedBiometricAuthenticationTypes =
      yield call(Biometrics.supportedAuthenticationTypes);
    /**
     * If biometrics enrollment key becomes corrupt, or there's an error fetching it,
     * we should not open biometrics automatically
     */
    if (hasBiometrics === INVALID_BIOMETRICS_ENROLLMENT_KEYCHAIN) {
      yield put(updateShouldBiometricsOpenAutomatically(false));
    }
    // If neither biometrics/pin are enabled for our app, remove any related info from SecureStore
    if (!isPinEnabled && !isBiometricLoginEnabled) {
      yield call(KeychainStore.deleteAll);
    }

    yield put(updateBiometricsAndPinInfo({
      isBiometricLoginEnabled,
      isPinEnabled,
      supportedBiometricAuthenticationTypes,
    }));
  } catch {
    yield put(updateBiometricsAndPinInfo({
      isBiometricLoginEnabled: false,
      isPinEnabled: false,
      supportedBiometricAuthenticationTypes: [],
    }));
  }
}

export function* disableBiometricLoginSaga() {
  try {
    yield call(KeychainStore.deleteBiometrics);
    yield put(disableBiometricLoginSuccess());
  } catch {
    yield put(disableBiometricLoginFailure({ error: DEFAULT_ERROR_MSG }));
  }
}

export function* disablePinSaga() {
  try {
    yield call(KeychainStore.deletePin);
    yield put(disablePinSuccess());
  } catch {
    yield put(disablePinFailure({ error: DEFAULT_ERROR_MSG }));
  }
}

export function* enableBiometricLoginSaga() {
  try {
    const isBiometricDevice = yield call(Biometrics.isBiometricDevice);
    if (isBiometricDevice) {
      const { error, success } = yield call(Biometrics.authenticate);
      if (error === 'user_cancel') {
        return;
      }
      if (!success) {
        throw new Error(BIOMETRICS_API_ERROR_MSG);
      }
      yield call(KeychainStore.setBiometricsEnrollment);
      yield put(enableBiometricLoginSuccess());
    } else {
      /*
        Either hardware malfunctioned, or Biometric is supported on the device but they have not
        enrolled on the device itself yet, or its iOS and they HAVE enrolled but they are currently
        locked out.
      */
      throw new Error(ENABLE_BIOMETRIC_LOGIN_ERROR_MSG);
    }
  } catch ({ message }) {
    const finalMessage =
      [BIOMETRICS_API_ERROR_MSG, ENABLE_BIOMETRIC_LOGIN_ERROR_MSG].includes(message) ?
        message :
        DEFAULT_ERROR_MSG;
    yield put(enableBiometricLoginFailure({ error: finalMessage }));
  }
}

export function* setPinSaga({ payload }) {
  const { pin } = payload;
  try {
    yield call(KeychainStore.setPin, pin);
    yield put(changeStep({ step: 1 }));
    yield put(setPinSuccess());
  } catch {
    yield put(setPinFailure({ error: DEFAULT_ERROR_MSG }));
  }
}

export default function* rootSaga() {
  yield all([
    takeLatest(CHECK_BIOMETRICS_AND_PIN_INFO, checkBiometricsAndPinInfoSaga),
    takeLatest(DISABLE_BIOMETRIC_LOGIN, disableBiometricLoginSaga),
    takeLatest(DISABLE_PIN, disablePinSaga),
    takeLatest(ENABLE_BIOMETRIC_LOGIN, enableBiometricLoginSaga),
    takeLatest(SET_PIN, setPinSaga),
  ]);
}
